import { useCallback, useMemo, useState } from 'react';

import Animated, { FadeIn } from 'react-native-reanimated';

import { ListCardGroup } from 'components/list-card-group';
import { ProductCard } from 'components/product-card';
import { useMenuCalories } from 'hooks/menu/use-menu-calories';
import { useUnifiedMenuContext } from 'state/unified-menu';
import { ILocalizedMenuEntry } from 'state/unified-menu/types';
import { useMenuQueryOptions } from 'state/unified-menu/use-menu-query-options';
import { getEntryPrice, stringifySanityImage } from 'state/unified-menu/utils';

import { useProductWizardContext } from '../../product-wizard';
import { useProductWizardFlow } from '../../product-wizard/use-product-wizard-flow';
import { FADE_IN_ANIMATION_MILLIS, SelectorContainer } from '../styled';

import { ProductCustomizationDrinksTabs } from './product-customization-drinks-tabs';
import { ProductCustomizationModifiers } from './product-customization-modifiers';
import { DrinksTabsContent } from './styled';
import { CustomizationRenderItem, DrinkCategory } from './types';
import { getComboSlotItems, getDrinkCategory } from './utils';

export const ProductCustomizationDrinks = () => {
  const { indexedMenu } = useUnifiedMenuContext();
  const { currentFlowStep } = useProductWizardFlow();
  const { slotsSelections, selectComboSlotItem, removeSlotSelection } = useProductWizardContext();
  const menuQueryOptions = useMenuQueryOptions();
  const { formatCalories } = useMenuCalories();

  const slotKey = currentFlowStep.slotKey!;
  const selectedSlot = slotsSelections[slotKey];

  const [selectedRow, setSelectedRow] = useState<number>();

  const cachedComboDrinks: ILocalizedMenuEntry[] = useMemo(
    () => getComboSlotItems(indexedMenu, slotKey, menuQueryOptions.posServiceMode),
    [indexedMenu, slotKey, menuQueryOptions.posServiceMode]
  );

  const availableCategories: DrinkCategory[] = useMemo(
    () =>
      cachedComboDrinks
        .reduce((categories, drink) => {
          const drinkCategory = getDrinkCategory(drink);
          if (!categories.includes(drinkCategory)) {
            categories.push(drinkCategory);
          }
          return categories;
        }, [] as DrinkCategory[])
        .sort(),
    [cachedComboDrinks]
  );

  const [activeTab, setActiveTab] = useState<DrinkCategory>(() => {
    const selectedDrink = cachedComboDrinks.find(({ id }) => id === selectedSlot?.itemId);
    return selectedDrink ? getDrinkCategory(selectedDrink) : availableCategories[0];
  });

  const filteredDrinks = useCallback(
    () => cachedComboDrinks.filter(drink => getDrinkCategory(drink) === activeTab),
    [activeTab, cachedComboDrinks]
  );

  return (
    <SelectorContainer>
      <ProductCustomizationDrinksTabs
        activeTab={activeTab}
        categories={availableCategories}
        onTabPress={activeTab => {
          removeSlotSelection(slotKey);
          setActiveTab(activeTab);
        }}
      />
      {useMemo(
        () => (
          <Animated.View
            key={`animation-${activeTab}`}
            entering={FadeIn.duration(FADE_IN_ANIMATION_MILLIS)}
          >
            <DrinksTabsContent>
              <ListCardGroup
                items={filteredDrinks()}
                key={`drink-list-${activeTab}`}
                selectedRow={selectedRow}
                ItemCustomizationComponent={<ProductCustomizationModifiers />}
                renderItem={({ item, index, rowIndex }: CustomizationRenderItem) => (
                  <ProductCard
                    key={`drink-${item.id}-${index}`}
                    isSelected={item.id === selectedSlot?.itemId}
                    productProps={{
                      name: item.name?.locale,
                      price: getEntryPrice(item, menuQueryOptions.posServiceMode),
                      calories: formatCalories(item.nutrition?.calories?.def ?? 0),
                      image: stringifySanityImage(item.image),
                    }}
                    onProductPress={() => {
                      selectComboSlotItem(slotKey, item.id);
                      setSelectedRow(rowIndex);
                    }}
                  />
                )}
              />
            </DrinksTabsContent>
          </Animated.View>
        ),
        [
          activeTab,
          filteredDrinks,
          formatCalories,
          menuQueryOptions.posServiceMode,
          selectComboSlotItem,
          selectedRow,
          selectedSlot?.itemId,
          slotKey,
        ]
      )}
    </SelectorContainer>
  );
};
